import { useRef, useCallback } from "react";
import { TapType, Coordinates } from "../types";
import { TAP_CONFIG } from "../constants";

type GetCoordinatesFn = (e: React.PointerEvent<HTMLDivElement>) => Coordinates;
type OnTapFn = (type: TapType, coordinates: Coordinates) => void;

/**
 * タップジェスチャー判定用カスタムフック
 *
 * シングルタップ、ダブルタップ、長押しを判定します。
 */
export const useTapDetection = (getRatio: GetCoordinatesFn, onTap: OnTapFn) => {
  // Return type: tap detection handlers
  const operationsRef = useRef<("pointerDown" | "pointerMove" | "pointerUp")[]>([]);
  const tapTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startCoordRef = useRef({ x: 0, y: 0 });
  const lastCoordRef = useRef({ xRatio: 0, yRatio: 0 });

  const resetState = useCallback(() => {
    if (tapTimeoutRef.current) {
      clearTimeout(tapTimeoutRef.current);
      tapTimeoutRef.current = null;
    }
    operationsRef.current = [];
  }, []);

  const judgeTap = useCallback((): TapType | void => {
    // ポインター移動がある場合はタップではない
    if (operationsRef.current.includes("pointerMove")) {
      return;
    }

    // ズーム操作（連続ポインターダウン）の検出
    const pointerDownCount = operationsRef.current.filter((op) => op === "pointerDown").length;
    if (pointerDownCount >= 2) {
      // 最後のダウンが最初のダウンの直後でない場合のみダブルタップ判定
      const downIndices = operationsRef.current
        .map((op, i) => (op === "pointerDown" ? i : -1))
        .filter((i) => i !== -1);

      if (downIndices.length >= 2) {
        const lastDownIdx = downIndices[downIndices.length - 1];
        const prevDownIdx = downIndices[downIndices.length - 2];
        if (lastDownIdx === prevDownIdx + 1) {
          return; // 連続ダウン＝ズーム操作
        }
      }

      return "double";
    }

    // 長押し判定
    if (!operationsRef.current.includes("pointerUp")) {
      return "long";
    }

    // シングルタップ
    return "single";
  }, []);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!tapTimeoutRef.current) {
        resetState();
        startCoordRef.current = { x: e.clientX, y: e.clientY };
        lastCoordRef.current = getRatio(e);

        tapTimeoutRef.current = setTimeout(() => {
          const tapType = judgeTap();
          if (tapType) {
            onTap(tapType, lastCoordRef.current);
          }
          resetState();
        }, TAP_CONFIG.SINGLE_TAP_MS);
      }

      operationsRef.current.push("pointerDown");
    },
    [getRatio, onTap, judgeTap, resetState]
  );

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const dx = e.clientX - startCoordRef.current.x;
    const dy = e.clientY - startCoordRef.current.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance > TAP_CONFIG.MOVE_THRESHOLD_PX) {
      operationsRef.current.push("pointerMove");
    }
  }, []);

  const handlePointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    operationsRef.current.push("pointerUp");
  }, []);

  const dispose = useCallback(() => {
    resetState();
  }, [resetState]);

  return {
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    dispose,
  };
};

export default useTapDetection;
